import api from './api';
import {
  Lead,
  LeadFilters,
  LeadStats,
  CreateLeadData,
  UpdateLeadData,
  LeadTimeline,
  LeadTask,
  LeadNote,
  CreateTaskData,
  CreateNoteData,
} from '@/types/lead.types';
import { PaginatedResponse, LeadResponse, TimelineResponse } from '@/types';

export const leadsService = {
  // Get all leads with filters
  getAll: (filters?: LeadFilters, page = 1, limit = 20) =>
    api.get<PaginatedResponse<Lead>>('/leads', { params: { ...filters, page, limit } }),

  // Get single lead - returns { lead: {...}, availableTransitions: [...] }
  getById: (id: string) =>
    api.get<LeadResponse>(`/leads/${id}`),

  // Create lead - API returns { lead: {...}, warning?: string }
  create: (data: CreateLeadData) =>
    api.post<{ lead: Lead; warning?: string }>('/leads', data),

  // Update lead
  update: (id: string, data: UpdateLeadData) =>
    api.put<Lead>(`/leads/${id}`, data),

  // Delete lead
  delete: (id: string) =>
    api.delete(`/leads/${id}`),

  // Update status
  updateStatus: (id: string, status: string) =>
    api.patch(`/leads/${id}/status`, { status }),

  // Get timeline - returns { timeline: [...] }
  getTimeline: (id: string) =>
    api.get<TimelineResponse<LeadTimeline>>(`/leads/${id}/timeline`),

  // Add task - returns { task: {...} }
  addTask: (id: string, data: CreateTaskData) =>
    api.post<{ task: LeadTask }>(`/leads/${id}/tasks`, data),

  // Complete task
  completeTask: (leadId: string, taskId: string) =>
    api.patch(`/leads/${leadId}/tasks/${taskId}/complete`),

  // Get tasks - returns { tasks: [...] }
  getTasks: (id: string) =>
    api.get<{ tasks: LeadTask[] }>(`/leads/${id}/tasks`),

  // Add note - returns { note: {...} }
  addNote: (id: string, data: CreateNoteData) =>
    api.post<{ note: LeadNote }>(`/leads/${id}/notes`, data),

  // Get notes - returns { notes: [...] }
  getNotes: (id: string) =>
    api.get<{ notes: LeadNote[] }>(`/leads/${id}/notes`),

  // Get stats
  getStats: () =>
    api.get<LeadStats>('/leads/stats'),

  // Check duplicate - API returns { isDuplicate: boolean, existingLead: {...} | null }
  checkDuplicate: (phone: string) =>
    api.get<{ isDuplicate: boolean; existingLead: Partial<Lead> | null }>('/leads/check-duplicate', { params: { phone } }),

  // Get available statuses
  getStatuses: () =>
    api.get<{ value: string; label: string }[]>('/leads/statuses'),
};

export default leadsService;
